import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User, Mail, Shield, LogOut, Key, Eye, EyeOff, Bell, BellOff, Compass } from "lucide-react";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import Footer from "@/components/Footer";
import { isPushSupported, requestNotificationPermission, registerServiceWorker, subscribeToPush, getLocalPushEndpoint } from "@/utils/notifications";
import SEO from "@/components/SEO";
import AccountAnnouncementBanner from "@/components/AccountAnnouncementBanner";
import { getImageUrl } from "@/utils/imageUtils";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const Account = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const action = searchParams.get("action"); // register or forgot

  const [user, setUser] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Newsletter subscription
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  
  // Account tabs - only account and notifications now
  const [activeTab, setActiveTab] = useState("account");

  // Notification preferences
  const [notificationPrefs, setNotificationPrefs] = useState({
    forum_replies: true,
    forum_mentions: true,
    chat_mentions: true,
    thread_reactions: false,
    reply_reactions: false,
    species_id_approved: true,
    trip_vote: true,
    new_trip: true,
    chat_messages: true,
    push_enabled: true
  });
  const [savingNotifPrefs, setSavingNotifPrefs] = useState(false);
  const [pushStatus, setPushStatus] = useState(null);
  const [sendingTestPush, setSendingTestPush] = useState(false);

  useEffect(() => {
    if (activeTab !== "notifications") return;
    refreshPushStatus();
  }, [activeTab]);

  const refreshPushStatus = async () => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) return;
    try {
      const endpoint = await getLocalPushEndpoint();
      const res = await axios.get(`${API}/push/status`, {
        headers: { Authorization: `Bearer ${token}` },
        params: endpoint ? { endpoint } : {},
      });
      setPushStatus({
        ...res.data,
        permission: typeof Notification !== "undefined" ? Notification.permission : "unsupported",
        supported: isPushSupported(),
      });
    } catch (e) {
      setPushStatus(null);
    }
  };

  const handleEnablePushHere = async () => {
    setSendingTestPush(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const granted = await requestNotificationPermission();
      if (!granted) {
        toast.error("Notifications are blocked for this site — allow them in your browser/app settings");
        return;
      }
      await registerServiceWorker();
      const sub = await subscribeToPush(token);
      toast[sub ? "success" : "error"](sub ? "This device is now subscribed" : "Could not subscribe this device");
      await refreshPushStatus();
    } finally {
      setSendingTestPush(false);
    }
  };

  const handleTestPush = async () => {
    setSendingTestPush(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const res = await axios.post(`${API}/push/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.sent ? "Test notification sent — check your device" : "Push send failed, see server logs");
      await refreshPushStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not send test notification");
    } finally {
      setSendingTestPush(false);
    }
  };

  // Login form
  const [loginData, setLoginData] = useState({ email: "", password: "", rememberMe: true });
  
  // Password change form
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Register form
  const [registerData, setRegisterData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: ""
  });

  const [showRegister, setShowRegister] = useState(action === "register");
  const [showForgot, setShowForgot] = useState(action === "forgot");

  useEffect(() => {
    if (action === "register") setShowRegister(true);
    if (action === "forgot") setShowForgot(true);
    
    // Handle tab query param
    const tabParam = searchParams.get("tab");
    if (tabParam === "notifications") {
      setActiveTab("notifications");
    }
  }, [action, searchParams]);

  // Refresh user state when component mounts or location changes
  useEffect(() => {
    setPageLoading(true);
    // Check both localStorage and sessionStorage for user data
    const currentUser = JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "null");
    setUser(currentUser);
    setPageLoading(false);
    
    // Check newsletter subscription status if user is logged in
    if (currentUser?.email) {
      checkNewsletterSubscription(currentUser.email);
    }
    
    // Fetch notification preferences if logged in
    if (currentUser?.id) {
      fetchNotificationPreferences();
    }
  }, [location]);

  const fetchNotificationPreferences = async () => {
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const res = await axios.get(`${API}/users/me/notification-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationPrefs(res.data);
    } catch (error) {
      console.error("Failed to load notification preferences:", error);
    }
  };

  const handleUpdateNotificationPrefs = async (key, value) => {
    const newPrefs = { ...notificationPrefs, [key]: value };
    setNotificationPrefs(newPrefs);
    
    try {
      setSavingNotifPrefs(true);
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      await axios.put(`${API}/users/me/notification-preferences`, { [key]: value }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Notification settings saved");
    } catch (error) {
      console.error("Failed to save notification preferences:", error);
      toast.error("Failed to save settings");
      // Revert
      setNotificationPrefs(prev => ({ ...prev, [key]: !value }));
    } finally {
      setSavingNotifPrefs(false);
    }
  };

  const checkNewsletterSubscription = async (email) => {
    try {
      const res = await axios.get(`${API}/newsletter/status?email=${encodeURIComponent(email)}`);
      setIsSubscribed(res.data.subscribed);
    } catch (error) {
      setIsSubscribed(false);
    }
  };

  const handleNewsletterToggle = async () => {
    if (!user?.email) return;
    
    setSubscriptionLoading(true);
    try {
      if (isSubscribed) {
        await axios.post(`${API}/newsletter/unsubscribe?email=${encodeURIComponent(user.email)}`);
        setIsSubscribed(false);
        toast.success("Unsubscribed from newsletter");
      } else {
        await axios.post(`${API}/newsletter/subscribe`, {
          email: user.email,
          name: user.name
        });
        setIsSubscribed(true);
        toast.success("Subscribed to newsletter!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update subscription");
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: loginData.email,
        password: loginData.password
      });
      
      // Store based on Remember Me preference
      const storage = loginData.rememberMe ? localStorage : sessionStorage;
      storage.setItem("user", JSON.stringify(response.data.user));
      storage.setItem("token", response.data.token);
      
      // Clear the other storage
      const otherStorage = loginData.rememberMe ? sessionStorage : localStorage;
      otherStorage.removeItem("user");
      otherStorage.removeItem("token");
      
      setUser(response.data.user);
      
      // Trigger event for FloatingUserMenu to refresh
      window.dispatchEvent(new Event('userLogin'));
      
      // Redirect to admin if admin
      if (response.data.user.role === "admin") {
        navigate("/admin");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError("");
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Password changed successfully!");
      setShowPasswordChange(false);
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");

    if (registerData.password !== registerData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, {
        name: registerData.name,
        email: registerData.email,
        password: registerData.password
      });
      localStorage.setItem("user", JSON.stringify(response.data.user));
      localStorage.setItem("token", response.data.token);
      setUser(response.data.user);
      setSuccess("Account created successfully!");
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("token");
    setUser(null);
    navigate("/");
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin": return "bg-red-100 text-red-700 border-red-300";
      case "pro": return "bg-purple-100 text-purple-700 border-purple-300";
      default: return "bg-blue-100 text-blue-700 border-blue-300";
    }
  };

  // Show loading state
  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600 text-xl">Loading...</div>
      </div>
    );
  }

  // If user is logged in, show account page
  if (user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SEO 
          title="My Account - George Dives"
          description="Manage your George Dives account"
        />
        
        <Navigation />
        <FloatingUserMenu />

        <section className="pt-32 pb-12 bg-gradient-to-br from-cyan-500 to-blue-600">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Account Settings
            </h1>
            <p className="text-lg text-white/90">
              Manage your account, privacy, and security
            </p>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4 max-w-2xl">
            {/* Account Page Announcements */}
            <AccountAnnouncementBanner />
            
            {/* Tab Navigation - Account and Notifications only */}
            <div className="flex gap-2 mb-6 flex-wrap">
              <Button
                variant={activeTab === "account" ? "default" : "outline"}
                onClick={() => setActiveTab("account")}
                className={activeTab === "account" ? "bg-cyan-600" : ""}
              >
                <User className="w-4 h-4 mr-2" />
                Account
              </Button>
              {user.role !== "dive_center" && (
                <Button
                  variant={activeTab === "notifications" ? "default" : "outline"}
                  onClick={() => setActiveTab("notifications")}
                  className={activeTab === "notifications" ? "bg-purple-600" : ""}
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Notifications
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => {
                  localStorage.removeItem(`walkthrough_completed_${user.id}`);
                  window.dispatchEvent(new CustomEvent('restartWalkthrough'));
                  toast.success("Walkthrough restarted! Navigate to the home page to begin.");
                }}
                data-testid="restart-walkthrough-btn"
              >
                <Compass className="w-4 h-4 mr-2" />
                Replay Tour
              </Button>
            </div>

            {/* Notifications Tab */}
            {activeTab === "notifications" && user.role !== "dive_center" && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-purple-600" />
                    Notification Settings
                  </CardTitle>
                  <CardDescription>
                    Choose which notifications you want to receive
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Forum Notifications */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">Forum Notifications</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">Thread Replies</p>
                          <p className="text-sm text-gray-500">Get notified when someone replies to your thread</p>
                        </div>
                        <button
                          onClick={() => handleUpdateNotificationPrefs("forum_replies", !notificationPrefs.forum_replies)}
                          disabled={savingNotifPrefs}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            notificationPrefs.forum_replies ? "bg-purple-600" : "bg-gray-200"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              notificationPrefs.forum_replies ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">Forum Mentions</p>
                          <p className="text-sm text-gray-500">Get notified when someone mentions you in a forum post</p>
                        </div>
                        <button
                          onClick={() => handleUpdateNotificationPrefs("forum_mentions", !notificationPrefs.forum_mentions)}
                          disabled={savingNotifPrefs}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            notificationPrefs.forum_mentions ? "bg-purple-600" : "bg-gray-200"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              notificationPrefs.forum_mentions ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">Thread Reactions</p>
                          <p className="text-sm text-gray-500">Get notified when someone reacts to your thread</p>
                        </div>
                        <button
                          onClick={() => handleUpdateNotificationPrefs("thread_reactions", !notificationPrefs.thread_reactions)}
                          disabled={savingNotifPrefs}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            notificationPrefs.thread_reactions ? "bg-purple-600" : "bg-gray-200"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              notificationPrefs.thread_reactions ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">Reply Reactions</p>
                          <p className="text-sm text-gray-500">Get notified when someone reacts to your reply</p>
                        </div>
                        <button
                          onClick={() => handleUpdateNotificationPrefs("reply_reactions", !notificationPrefs.reply_reactions)}
                          disabled={savingNotifPrefs}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            notificationPrefs.reply_reactions ? "bg-purple-600" : "bg-gray-200"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              notificationPrefs.reply_reactions ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Chat Notifications */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">Chat Notifications</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">New Chat Messages</p>
                          <p className="text-sm text-gray-500">Push notification when someone writes in chat while you're away (max one every 2 min per room)</p>
                        </div>
                        <button
                          onClick={() => handleUpdateNotificationPrefs("chat_messages", !(notificationPrefs.chat_messages !== false))}
                          disabled={savingNotifPrefs}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            notificationPrefs.chat_messages !== false ? "bg-purple-600" : "bg-gray-200"
                          }`}
                          data-testid="pref-chat-messages-toggle"
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              notificationPrefs.chat_messages !== false ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">Chat Mentions</p>
                          <p className="text-sm text-gray-500">Get notified when someone mentions you in chat</p>
                        </div>
                        <button
                          onClick={() => handleUpdateNotificationPrefs("chat_mentions", !notificationPrefs.chat_mentions)}
                          disabled={savingNotifPrefs}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            notificationPrefs.chat_mentions ? "bg-purple-600" : "bg-gray-200"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              notificationPrefs.chat_mentions ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Marine Database Notifications */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">Marine Database Notifications</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">Species ID Approved</p>
                          <p className="text-sm text-gray-500">Get notified when your species ID submission is reviewed and identified</p>
                        </div>
                        <button
                          onClick={() => handleUpdateNotificationPrefs("species_id_approved", !notificationPrefs.species_id_approved)}
                          disabled={savingNotifPrefs}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            notificationPrefs.species_id_approved !== false ? "bg-purple-600" : "bg-gray-200"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              notificationPrefs.species_id_approved !== false ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">Trip Votes</p>
                          <p className="text-sm text-gray-500">Get notified when someone votes on your trip proposal</p>
                        </div>
                        <button
                          onClick={() => handleUpdateNotificationPrefs("trip_vote", !notificationPrefs.trip_vote)}
                          disabled={savingNotifPrefs}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            notificationPrefs.trip_vote !== false ? "bg-purple-600" : "bg-gray-200"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              notificationPrefs.trip_vote !== false ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">New Trips</p>
                          <p className="text-sm text-gray-500">Get notified when a new trip is proposed by the community</p>
                        </div>
                        <button
                          onClick={() => handleUpdateNotificationPrefs("new_trip", !notificationPrefs.new_trip)}
                          disabled={savingNotifPrefs}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            notificationPrefs.new_trip !== false ? "bg-purple-600" : "bg-gray-200"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              notificationPrefs.new_trip !== false ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>
                      <div className="flex items-center justify-between p-4 border rounded-lg border-cyan-200 bg-cyan-50/50">
                        <div>
                          <p className="font-medium">Push Notifications</p>
                          <p className="text-sm text-gray-500">Receive browser push notifications for all enabled events above</p>
                          {pushStatus && (
                            <div className="mt-2 space-y-1 text-xs" data-testid="push-status-text">
                              <p className={pushStatus.this_device_subscribed ? "text-green-700 font-medium" : "text-amber-700 font-medium"}>
                                {!pushStatus.supported
                                  ? "This browser does not support push notifications"
                                  : pushStatus.this_device_subscribed
                                    ? "✓ This device is subscribed"
                                    : pushStatus.permission === "denied"
                                      ? "✗ Notifications are blocked for this site on this device"
                                      : "✗ This device is not subscribed yet"}
                              </p>
                              <p className="text-gray-500">
                                {pushStatus.subscriptions} device{pushStatus.subscriptions !== 1 ? "s" : ""} subscribed in total · {pushStatus.unread_count} unread
                              </p>
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2 mt-2">
                            {pushStatus && pushStatus.supported && !pushStatus.this_device_subscribed && pushStatus.permission !== "denied" && (
                              <Button type="button" size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white" disabled={sendingTestPush} onClick={handleEnablePushHere} data-testid="enable-push-btn">
                                Enable on this device
                              </Button>
                            )}
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={sendingTestPush}
                              onClick={handleTestPush}
                              data-testid="test-push-btn"
                            >
                              {sendingTestPush ? "Working..." : "Send test notification"}
                            </Button>
                          </div>
                        </div>
                        <button
                          onClick={() => handleUpdateNotificationPrefs("push_enabled", !notificationPrefs.push_enabled)}
                          disabled={savingNotifPrefs}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            notificationPrefs.push_enabled !== false ? "bg-purple-600" : "bg-gray-200"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              notificationPrefs.push_enabled !== false ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Account Tab (default) */}
            {activeTab === "account" && (
            <Card>
              <CardHeader className="text-center">
                <div className="flex justify-center mb-6">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 shadow-lg flex items-center justify-center overflow-hidden">
                    {user.profile_pic ? (
                      <img src={getImageUrl(user.profile_pic)} alt={user.name} className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-12 h-12 text-white" />
                    )}
                  </div>
                </div>
                <CardTitle className="text-3xl">{user.name}</CardTitle>
                <div className="flex justify-center mt-4">
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold border-2 ${getRoleBadgeColor(user.role)}`}>
                    {user.role.toUpperCase()}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    <Mail className="w-5 h-5 text-cyan-600" />
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-semibold">{user.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    <Shield className="w-5 h-5 text-cyan-600" />
                    <div>
                      <p className="text-sm text-gray-600">Account Type</p>
                      <p className="font-semibold capitalize">{user.role}</p>
                    </div>
                  </div>
                </div>

                {/* Password Change Section */}
                {!showPasswordChange ? (
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    <Key className="w-5 h-5 text-cyan-600" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-600">Password</p>
                      <p className="font-semibold">••••••••</p>
                    </div>
                    <Button
                      onClick={() => setShowPasswordChange(true)}
                      variant="outline"
                      size="sm"
                      className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                    >
                      Change
                    </Button>
                  </div>
                ) : (
                  <Card className="border-cyan-200 bg-cyan-50/50">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Key className="w-5 h-5 text-cyan-600" />
                        Change Password
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handlePasswordChange} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="currentPassword">Current Password</Label>
                          <div className="relative">
                            <Input
                              id="currentPassword"
                              type={showCurrentPassword ? "text" : "password"}
                              value={passwordData.currentPassword}
                              onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                              required
                              className="pr-10"
                            />
                            <button
                              type="button"
                              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            >
                              {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="newPassword">New Password</Label>
                          <div className="relative">
                            <Input
                              id="newPassword"
                              type={showNewPassword ? "text" : "password"}
                              value={passwordData.newPassword}
                              onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                              required
                              minLength={6}
                              className="pr-10"
                            />
                            <button
                              type="button"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            >
                              {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="confirmNewPassword">Confirm New Password</Label>
                          <Input
                            id="confirmNewPassword"
                            type="password"
                            value={passwordData.confirmPassword}
                            onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                            required
                          />
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button
                            type="submit"
                            disabled={passwordLoading}
                            className="bg-cyan-500 hover:bg-cyan-600"
                          >
                            {passwordLoading ? "Saving..." : "Update Password"}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setShowPasswordChange(false);
                              setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                )}

                {/* Newsletter Subscription */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {isSubscribed ? (
                      <Bell className="w-5 h-5 text-cyan-600" />
                    ) : (
                      <BellOff className="w-5 h-5 text-gray-400" />
                    )}
                    <div>
                      <p className="font-medium">Monthly Newsletter</p>
                      <p className="text-sm text-gray-500">
                        {isSubscribed ? "You're subscribed to monthly updates" : "Get monthly diving updates"}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handleNewsletterToggle}
                    disabled={subscriptionLoading}
                    variant={isSubscribed ? "outline" : "default"}
                    size="sm"
                    className={isSubscribed ? "" : "bg-cyan-500 hover:bg-cyan-600"}
                  >
                    {subscriptionLoading ? "..." : isSubscribed ? "Unsubscribe" : "Subscribe"}
                  </Button>
                </div>

                <div className="pt-6 border-t flex flex-col gap-3">
                  {user.role === "admin" && (
                    <Button
                      onClick={() => navigate("/admin")}
                      className="w-full bg-red-500 hover:bg-red-600"
                    >
                      Go to Admin Dashboard
                    </Button>
                  )}
                  <Button
                    onClick={() => navigate(`/profile/${user.id}`)}
                    variant="outline"
                    className="w-full border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                  >
                    <User className="w-4 h-4 mr-2" />
                    View My Profile
                  </Button>
                  <Button
                    onClick={() => navigate("/privacy-settings")}
                    variant="outline"
                    className="w-full border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Privacy Settings
                  </Button>
                  <Button
                    onClick={handleLogout}
                    variant="outline"
                    className="w-full"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </Button>
                </div>
              </CardContent>
            </Card>
            )}
          </div>
        </section>
        
        <Footer />
      </div>
    );
  }

  // If not logged in, show login/register forms
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Login - George Dives"
        description="Sign in to your George Dives account"
      />
      
      <Navigation />
      <FloatingUserMenu />

      <section className="pt-32 pb-16 bg-gradient-to-br from-cyan-500 to-blue-600">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            {showRegister ? "Create Account" : showForgot ? "Reset Password" : "Welcome Back"}
          </h1>
          <p className="text-lg text-white/90">
            {showRegister ? "Join the diving community" : showForgot ? "We'll help you reset your password" : "Sign in to your account"}
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4 max-w-md">
          {!showRegister && !showForgot && (
            <Card>
              <CardHeader>
                <CardTitle>Sign In</CardTitle>
                <CardDescription>Enter your credentials to access your account</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email or Username</Label>
                    <Input
                      id="email"
                      type="text"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      placeholder="Enter your email or username"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="rememberMe"
                      checked={loginData.rememberMe}
                      onChange={(e) => setLoginData({ ...loginData, rememberMe: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <Label htmlFor="rememberMe" className="text-sm text-gray-600 cursor-pointer">
                      Remember me
                    </Label>
                  </div>

                  {error && (
                    <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                      {error}
                    </div>
                  )}

                  <Button type="submit" className="w-full bg-cyan-500 hover:bg-cyan-600" disabled={loading}>
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>

                  <div className="flex flex-col gap-2 pt-4 border-t">
                    <button
                      type="button"
                      onClick={() => { setShowForgot(true); setError(""); }}
                      className="text-sm text-gray-600 hover:text-gray-700 text-center"
                    >
                      Forgot password?
                    </button>
                    <div className="text-center pt-3 border-t mt-3">
                      <p className="text-xs text-gray-500 mb-2">
                        Don&apos;t have an account?
                      </p>
                      <Link
                        to="/request-invite"
                        className="text-sm text-cyan-600 hover:text-cyan-700 font-medium flex items-center justify-center gap-2 w-full"
                        data-testid="ask-invitation-btn"
                      >
                        <Mail className="w-4 h-4" />
                        Ask for an invitation
                      </Link>
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {showRegister && (
            <Card>
              <CardHeader>
                <CardTitle>Create Account</CardTitle>
                <CardDescription>Fill in your details to register</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={registerData.name}
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="reg-email">Email</Label>
                    <Input
                      id="reg-email"
                      type="email"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="reg-password">Password</Label>
                    <Input
                      id="reg-password"
                      type="password"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={registerData.confirmPassword}
                      onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                      required
                    />
                  </div>

                  {error && (
                    <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                      {error}
                    </div>
                  )}

                  {success && (
                    <div className="text-sm text-green-600 bg-green-50 p-3 rounded-lg">
                      {success}
                    </div>
                  )}

                  <Button type="submit" className="w-full bg-cyan-500 hover:bg-cyan-600" disabled={loading}>
                    {loading ? "Creating account..." : "Create Account"}
                  </Button>

                  <button
                    type="button"
                    onClick={() => { setShowRegister(false); setError(""); setSuccess(""); }}
                    className="text-sm text-cyan-600 hover:text-cyan-700 font-medium text-center w-full pt-4"
                  >
                    ← Back to login
                  </button>
                </form>
              </CardContent>
            </Card>
          )}

          {showForgot && (
            <Card>
              <CardHeader>
                <CardTitle>Reset Password</CardTitle>
                <CardDescription>Enter your email to receive reset instructions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="forgot-email">Email</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-800">
                      Password reset functionality is coming soon. Please contact support at info@georgedives.com for assistance.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => { setShowForgot(false); setError(""); }}
                    className="text-sm text-cyan-600 hover:text-cyan-700 font-medium text-center w-full pt-4"
                  >
                    ← Back to login
                  </button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Account;
